import { useState } from "react";
import { createPortal } from "react-dom";
import { PALETTE as P } from "../tokens";

// Pre-permission explainer — the screen that says WHY Haiven notifies
// BEFORE any native permission dialog fires. iOS gives one shot at the
// real prompt per install; spending it without context is how ~40% of
// asks die. This overlay carries the ask, and only the primary CTA
// hands off to the platform permission flow (Web Push on PWA/browser,
// APNs via Capacitor in the iOS shell — the caller passes the
// platform-branched enable function).
//
// Props:
//   onEnable  — async () => status ('subscribed'|'denied'|...) — fires
//               the real permission request; called only from the
//               primary CTA.
//   onClose   — (result: 'subscribed'|'denied'|'dismissed'|'install-required'|'error')
//               — invoked when the overlay is done; caller owns
//               dismissal persistence and any toast.
export default function PushPrePrompt({ onEnable, onClose }) {
  const [requesting, setRequesting] = useState(false);

  const handleEnable = async () => {
    if (requesting) return;
    setRequesting(true);
    const result = await onEnable();
    setRequesting(false);
    onClose(
      result === "subscribed" || result === "denied" || result === "install-required"
        ? result
        : "error"
    );
  };

  // Portal to <body>: the card that opens this overlay animates with a
  // transform (className "fu"), which makes it the containing block for
  // position:fixed descendants — rendered inline, the overlay would pin
  // to the card and let the dashboard bleed through.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Turn on reminders"
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: P.bg,
        display: "flex", flexDirection: "column", justifyContent: "center",
        padding: "0 28px", maxWidth: 420, margin: "0 auto",
      }}
    >
      <div style={{
        fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase",
        color: P.primary, fontWeight: 600, marginBottom: 16,
      }}>
        Reminders
      </div>
      <h1 className="serif" style={{
        fontFamily: "'Playfair Display', Georgia, serif",
        fontSize: 32, fontStyle: "italic", fontWeight: 400,
        color: P.text, lineHeight: 1.2, marginBottom: 14,
      }}>
        A nudge at the right moment.
      </h1>
      <p style={{
        fontSize: 14.5, color: P.textMid, fontWeight: 300,
        lineHeight: 1.7, marginBottom: 10, maxWidth: 320,
      }}>
        Haiven works because you show up daily — your signal is built from
        check-ins, and the pattern only forms if the days connect.
      </p>
      <p style={{
        fontSize: 14.5, color: P.textMid, fontWeight: 300,
        lineHeight: 1.7, marginBottom: 34, maxWidth: 320,
      }}>
        One quiet reminder in your check-in window. Never more, no noise —
        and you can change the timing anytime in your profile.
      </p>
      <button
        type="button"
        onClick={handleEnable}
        disabled={requesting}
        aria-busy={requesting ? "true" : "false"}
        style={{
          width: "100%", padding: 17, background: P.dark,
          border: "none", borderRadius: 16, cursor: requesting ? "default" : "pointer",
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: 18, fontStyle: "italic", color: P.bg,
          opacity: requesting ? 0.6 : 1, transition: "opacity 0.15s",
        }}
      >
        {requesting ? "One moment…" : "Turn on reminders"}
      </button>
      <button
        type="button"
        onClick={() => onClose("dismissed")}
        style={{
          background: "none", border: "none", marginTop: 18,
          color: P.textMute, fontSize: 13, cursor: "pointer",
          padding: 10, fontFamily: "'DM Sans', sans-serif",
        }}
      >
        Not now
      </button>
    </div>,
    document.body
  );
}
